import React, { useState } from "react";
import { ActionIcon, Badge, Group, Text, ThemeIcon, Tooltip } from "@mantine/core";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaPauseCircle,
  FaTable,
} from "react-icons/fa";
import { selectSpaceValidationsMatrixForSelectedRoomsExcludingStandardRoom } from "./selectedRoomDevicesSelectors";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  setActiveLeftSideBarTabInRedux,
  setRunSpaceValidationsInSelectedRoomsInRedux,
} from "../../designSlice"; // ⚠️ juster path
import { SelectedRoomsValidationsModal } from "./SelectedRoomsValidationsModal"; // ⚠️ juster path

export const SelectedRoomsValidationSummaryInline: React.FC = () => {
  const dispatch = useAppDispatch();
  const matrix = useAppSelector(selectSpaceValidationsMatrixForSelectedRoomsExcludingStandardRoom);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const selectedCount = matrix.selectedRoomsCount;
  if (selectedCount === 0) return null;

  const roomsWithDevices = matrix.roomsWithDevicesCount;
  const approved = matrix.approvedRoomsCount;
  const notApproved = matrix.notApprovedRoomsCount;

  const stopRunningRules = () => {
    dispatch(setRunSpaceValidationsInSelectedRoomsInRedux(false));
    dispatch(setActiveLeftSideBarTabInRedux(undefined));
  };

  const openDetails = () => setDetailsOpen(true);
  const closeDetails = () => setDetailsOpen(false);

  const allApproved = roomsWithDevices > 0 && notApproved === 0;

  const Wrapper: React.FC<React.PropsWithChildren> = ({ children }) => (
    <Group
      gap="xs"
      align="center"
      wrap="nowrap"
      px={8}
      py={6}
      style={{
        background: "rgba(255,255,255,0.9)",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 999,
      }}
    >
      {children}
    </Group>
  );

  // Ingen rom med komponenter -> nøytral info
  if (roomsWithDevices === 0) {
    return (
      <>
        <Wrapper>
          <ThemeIcon variant="light" size="sm" color="gray">
            <FaInfoCircle size={14} />
          </ThemeIcon>

          <Text size="sm" fw={600}>
            Ingen valgte rom har komponenter
          </Text>

          <Tooltip label="Vis detaljer" withArrow>
            <ActionIcon
              variant="light"
              color="gray"
              onClick={openDetails}
              aria-label="Vis detaljer"
              size="sm"
            >
              <FaTable size={16} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Stopp kjøring av regler for valgte rom" withArrow>
            <ActionIcon
              variant="light"
              color="gray"
              onClick={stopRunningRules}
              aria-label="Stopp regler"
              size="sm"
            >
              <FaPauseCircle size={16} />
            </ActionIcon>
          </Tooltip>
        </Wrapper>

        <SelectedRoomsValidationsModal opened={detailsOpen} onClose={closeDetails} />
      </>
    );
  }

  return (
    <>
      <Wrapper>
        <Tooltip
          label={
            allApproved
              ? "Alle rom med komponenter oppfyller alle krav"
              : "Noen rom med komponenter oppfyller ikke alle krav"
          }
          withArrow
        >
          <ThemeIcon variant="light" size="sm" color={allApproved ? "green" : "red"}>
            {allApproved ? <FaCheckCircle size={14} /> : <FaExclamationTriangle size={14} />}
          </ThemeIcon>
        </Tooltip>

        <Badge variant="filled" color="green" radius="sm">
          Godkjent: {approved}
        </Badge>

        <Badge variant="filled" color={notApproved > 0 ? "red" : "gray"} radius="sm">
          Ikke godkjent: {notApproved}
        </Badge>

        <Text size="xs" c="dimmed">
          (av {roomsWithDevices} rom med komponenter)
        </Text>

        <Tooltip label="Vis detaljer" withArrow>
          <ActionIcon
            variant="light"
            color="gray"
            onClick={openDetails}
            aria-label="Vis detaljer"
            size="sm"
          >
            <FaTable size={16} />
          </ActionIcon>
        </Tooltip>

        <Tooltip label="Stopp kjøring av regler for valgte rom" withArrow>
          <ActionIcon
            variant="light"
            color="gray"
            onClick={stopRunningRules}
            aria-label="Stopp regler"
            size="sm"
          >
            <FaPauseCircle size={16} />
          </ActionIcon>
        </Tooltip>
      </Wrapper>

      <SelectedRoomsValidationsModal opened={detailsOpen} onClose={closeDetails} />
    </>
  );
};
